import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';

import PlotGroupSelector from './PlotGroupSelector';
import GroupRiskMetricsBarChart from './GroupRiskMetricsBarChart';
import GroupRiskMetricsTable from './GroupRiskMetricsTable';
import SortControl from './SortControl';
import RiskWorkbookExportButton from '../risk_overview/RiskWorkbookExportButton';
import CompactPanelHeader from '../commonComponents/CompactPanelHeader';
import { compactIconButtonSx, compactSelectSx } from '../pos_sizing_table/tableStyles';
import { ACTUAL_BAR_COLOR, OPTIMIZED_BAR_COLOR } from '../../utils/riskOverviewStyles';
import { MSI_TYPE_SCALE, usesAppStyleGrammar } from '../../styles/msiStyleTokens';
import {
    GROUP_METRICS,
    toNullableNumber,
} from '../../utils/riskOverviewPresentation';

const DEFAULT_METRIC_KEY = 'beta_exp';
const METRIC_LABELS = Object.fromEntries(
    GROUP_METRICS.map(({ metricKey, label }) => [metricKey, label]),
);
const METRIC_ABS_KEYS = Object.fromEntries(
    GROUP_METRICS.map(({ metricKey, absKey }) => [metricKey, absKey]),
);
const EMPTY_METRICS = Object.freeze({});
const EMPTY_ROWS = Object.freeze([]);
const NONFATAL_IDENTITY_STATES = new Set(['IDENTITY_PARTIAL', 'IDENTITY_UNRESOLVED_ALL']);

const normalizeStatus = (value) => String(value || '').trim().toUpperCase();

const deriveRenderableSuccess = ({ success, status, hasRows }) => (
    success === true
    || status === 'SUCCESS'
    || (status === 'IDENTITY_PARTIAL' && hasRows)
);

export default function GroupRiskMetricsTableAndPlot({
    actGroupRiskMetrics,
    optimizedGroupRiskMetrics,
    groupRisksLoading,
    optimizedGroupRiskMetricsLoading,
    optimalPortfolioLoaded,
    presentation,
    directExportEnabled,
    title,
    groupLabel,
    groupLabelPlural,
    headerTabs = null,
    exportFileName,
    exportSheetName,
}) {
    const actualMetrics = actGroupRiskMetrics ?? EMPTY_METRICS;
    const selectedMetrics = optimizedGroupRiskMetrics ?? EMPTY_METRICS;
    const actualRows = Array.isArray(actualMetrics.group_risks)
        ? actualMetrics.group_risks
        : EMPTY_ROWS;
    const selectedRows = Array.isArray(selectedMetrics.group_risks)
        ? selectedMetrics.group_risks
        : EMPTY_ROWS;
    const groupNames = useMemo(() => [...new Set([
        ...(Array.isArray(actualMetrics.group_names) ? actualMetrics.group_names : []),
        ...presentation.groupNames,
    ].map((name) => String(name || '').trim()).filter(Boolean))]
        .sort((left, right) => left.localeCompare(right)), [
        actualMetrics.group_names,
        presentation.groupNames,
    ]);

    const [selectedGroup, setSelectedGroup] = useState(groupNames[0] || '');
    const [selectedMetric, setSelectedMetric] = useState(DEFAULT_METRIC_KEY);
    const [includedGroups, setIncludedGroups] = useState(groupNames);
    const [sortKey, setSortKey] = useState('actual');
    const [sortOrder, setSortOrder] = useState('desc');
    const activeGroup = groupNames.includes(selectedGroup)
        ? selectedGroup
        : (groupNames[0] || '');

    useEffect(() => {
        setIncludedGroups((current) => {
            const retained = current.filter((category) => groupNames.includes(category));
            return retained.length > 0 ? retained : groupNames;
        });
    }, [groupNames]);

    const actualStatus = normalizeStatus(actualMetrics.status);
    const selectedStatus = normalizeStatus(selectedMetrics.status);
    const actualSuccess = deriveRenderableSuccess({
        success: actualMetrics.success,
        status: actualStatus,
        hasRows: actualRows.length > 0,
    });
    const selectedSuccess = deriveRenderableSuccess({
        success: selectedMetrics.success,
        status: selectedStatus,
        hasRows: selectedRows.length > 0,
    });
    const hasSelected = selectedRows.length > 0;
    const metricKey = METRIC_ABS_KEYS[selectedMetric];
    const actualPlotData = useMemo(() => includedGroups.map((category) => ({
        category,
        value: toNullableNumber(presentation.actualByGroup.get(category)?.[metricKey]),
    })), [includedGroups, metricKey, presentation.actualByGroup]);
    const selectedPlotData = useMemo(() => (
        hasSelected
            ? includedGroups.map((category) => ({
                category,
                value: toNullableNumber(presentation.selectedByGroup.get(category)?.[metricKey]),
            }))
            : EMPTY_ROWS
    ), [hasSelected, includedGroups, metricKey, presentation.selectedByGroup]);
    const exportSheets = useMemo(() => ([{
        name: exportSheetName,
        kind: 'json',
        rows: presentation.exportRows,
    }]), [exportSheetName, presentation.exportRows]);
    const isLoading = groupRisksLoading || optimizedGroupRiskMetricsLoading;
    const actualMessage = String(actualMetrics.message || '').trim();
    const selectedMessage = String(selectedMetrics.message || '').trim();
    const showIdentityGuidance = !groupRisksLoading
        && NONFATAL_IDENTITY_STATES.has(actualStatus)
        && Boolean(actualMessage || actualStatus === 'IDENTITY_UNRESOLVED_ALL');
    const showSelectedError = !optimizedGroupRiskMetricsLoading
        && optimalPortfolioLoaded
        && !selectedSuccess
        && actualSuccess
        && Boolean(selectedMessage);

    return (
        <>
            <CompactPanelHeader
                title={title}
                leading={(
                    <>
                        {headerTabs}
                        <FormControl size="small" sx={{ minWidth: 170, ...compactSelectSx }}>
                            <InputLabel id="category-select-label">Select {groupLabel}</InputLabel>
                            <Select
                                labelId="category-select-label"
                                value={activeGroup}
                                label={`Select ${groupLabel}`}
                                onChange={(event) => setSelectedGroup(event.target.value)}
                            >
                                {groupNames.length > 0 ? groupNames.map((category) => (
                                    <MenuItem key={category} value={category}>{category}</MenuItem>
                                )) : <MenuItem disabled>No data available</MenuItem>}
                            </Select>
                        </FormControl>
                    </>
                )}
            >
                <Typography
                    variant="body2"
                    sx={{
                        mr: 0.5,
                        color: 'text.secondary',
                        fontSize: (theme) => (
                            usesAppStyleGrammar(theme) ? MSI_TYPE_SCALE.helper.fontSize : '0.8rem'
                        ),
                    }}
                >
                    {METRIC_LABELS[selectedMetric]}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mr: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: ACTUAL_BAR_COLOR }} />
                    <Typography variant="caption">Actual</Typography>
                    {hasSelected && (
                        <>
                            <Box sx={{ width: 10, height: 10, bgcolor: OPTIMIZED_BAR_COLOR }} />
                            <Typography variant="caption">Optimized</Typography>
                        </>
                    )}
                </Box>
                <SortControl
                    compact
                    sortKey={sortKey}
                    sortOrder={sortOrder}
                    showOptimized={hasSelected}
                    onChange={(nextKey, nextOrder) => {
                        setSortKey(nextKey);
                        setSortOrder(nextOrder);
                    }}
                />
                <PlotGroupSelector
                    groupNames={groupNames}
                    includedGroups={includedGroups}
                    onChange={(event) => setIncludedGroups(event.target.value)}
                    groupLabelPlural={groupLabelPlural}
                />
                <RiskWorkbookExportButton
                    tooltip={`Download ${groupLabel.toLowerCase()} metrics`}
                    ariaLabel={`Download ${groupLabel.toLowerCase()} metrics`}
                    fileName={exportFileName}
                    sheets={exportSheets}
                    disabled={
                        !directExportEnabled
                        || isLoading
                        || presentation.exportRows.length === 0
                    }
                    sx={compactIconButtonSx}
                />
            </CompactPanelHeader>

            {showIdentityGuidance && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                    {actualMessage || 'No securities resolved into attribute-risk identity scope.'}
                </Alert>
            )}
            {showSelectedError && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                    Error loading optimized portfolio: {selectedMessage}
                </Alert>
            )}

            <Box display="flex" flexDirection={{ xs: 'column', lg: 'row' }} gap={4}>
                <Box flex={1} minWidth={0}>
                    <GroupRiskMetricsTable
                        actualByGroup={presentation.actualByGroup}
                        selectedByGroup={presentation.selectedByGroup}
                        selectedGroup={activeGroup}
                        selectedMetric={selectedMetric}
                        onMetricClick={setSelectedMetric}
                        actualLoading={groupRisksLoading}
                        optimizedLoading={optimizedGroupRiskMetricsLoading}
                        groupLabel={groupLabel}
                    />
                </Box>
                <Box
                    flex={1}
                    minWidth={0}
                    role="region"
                    aria-label={`${groupLabel} chart`}
                    aria-busy={isLoading || undefined}
                    sx={(theme) => ({
                        height: 360,
                        minHeight: 360,
                        [theme.breakpoints.up('lg')]: { height: 'auto' },
                    })}
                >
                    <GroupRiskMetricsBarChart
                        groupNames={includedGroups}
                        data={actualPlotData}
                        optData={selectedPlotData}
                        selectedGroup={activeGroup}
                        metricLabel={METRIC_LABELS[selectedMetric]}
                        onGroupSelect={setSelectedGroup}
                        sortKey={sortKey}
                        sortOrder={sortOrder}
                        groupLabel={groupLabel}
                        isLoading={isLoading}
                    />
                </Box>
            </Box>
        </>
    );
}

GroupRiskMetricsTableAndPlot.propTypes = {
    actGroupRiskMetrics: PropTypes.object,
    optimizedGroupRiskMetrics: PropTypes.object,
    groupRisksLoading: PropTypes.bool.isRequired,
    optimizedGroupRiskMetricsLoading: PropTypes.bool.isRequired,
    optimalPortfolioLoaded: PropTypes.bool.isRequired,
    presentation: PropTypes.shape({
        actualByGroup: PropTypes.instanceOf(Map).isRequired,
        selectedByGroup: PropTypes.instanceOf(Map).isRequired,
        groupNames: PropTypes.array.isRequired,
        exportRows: PropTypes.array.isRequired,
    }).isRequired,
    directExportEnabled: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    groupLabel: PropTypes.string.isRequired,
    groupLabelPlural: PropTypes.string.isRequired,
    headerTabs: PropTypes.node,
    exportFileName: PropTypes.string.isRequired,
    exportSheetName: PropTypes.string.isRequired,
};
